import { useState, useEffect, useRef } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { ShoppingCart, Menu, X, ArrowUpRight, UserRound, ChevronDown } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { PORTAL_URL } from "@/lib/site";
import { useShopCategories } from "@/lib/shopCategories";

export const Logo = ({ compact = false }) => (
  <Link to="/" data-testid="logo-link" className="flex items-center gap-2.5 shrink-0">
    <img src="/assets/logo/emblem-160.webp" alt="Regal Tuning logo" width="156" height="160" className="h-10 w-auto" />
    {!compact && (
      <span className="hidden sm:inline font-display text-lg font-extrabold tracking-[0.12em] leading-none text-ink">
        REGAL<span className="text-regal-amber">TUNING</span>
      </span>
    )}
  </Link>
);

const LINKS_BEFORE_SHOP = [
  { to: "/", label: "Home" },
  { to: "/tuning-files", label: "Tuning Files" },
  { to: "/tuning-database", label: "Tuning Database" },
  { to: "/tuning-tools", label: "Tuning Tools" },
];

const LINKS_AFTER_SHOP = [
  { to: "/become-a-dealer", label: "Become a Dealer" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
];

export default function Header() {
  const { taxonomy, error: categoryError, retry: retryCategories } = useShopCategories();
  const shopMenu = (taxonomy?.groups || []).filter(group => group.menu);
  const { count, setOpen } = useCart();
  const [mobile, setMobile] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [shopOpen, setShopOpen] = useState(false);
  const [mobileShop, setMobileShop] = useState(false);
  const [mobileBrand, setMobileBrand] = useState(null);
  const shopTimer = useRef(null);
  const { pathname, search } = useLocation();

  useEffect(() => { setMobile(false); setShopOpen(false); setMobileShop(false); setMobileBrand(null); }, [pathname, search]);
  useEffect(() => {
    const on = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", on, { passive: true });
    return () => window.removeEventListener("scroll", on);
  }, []);
  useEffect(() => {
    const esc = (e) => { if (e.key === "Escape") setShopOpen(false); };
    window.addEventListener("keydown", esc);
    return () => window.removeEventListener("keydown", esc);
  }, []);

  const openShop = () => { clearTimeout(shopTimer.current); setShopOpen(true); };
  const closeShop = () => { shopTimer.current = setTimeout(() => setShopOpen(false), 140); };

  const navCls = ({ isActive }) =>
    `font-display text-[13px] font-semibold uppercase tracking-[0.14em] transition-colors duration-300 hover:text-regal-amber ${isActive ? "text-regal-amber" : "text-neutral-300"}`;

  return (
    <header data-testid="site-header" className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${scrolled || mobile ? "glass" : "bg-transparent border-b border-transparent"}`}>
      <div className="container-regal flex h-16 sm:h-20 items-center justify-between gap-4">
        <Logo />
        <nav className="hidden lg:flex items-center gap-7" data-testid="desktop-nav">
          {LINKS_BEFORE_SHOP.map((l) => <NavLink key={l.to} to={l.to} end={l.to === "/"} className={navCls} data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}>{l.label}</NavLink>)}
          <div className="relative" onMouseEnter={openShop} onMouseLeave={closeShop}>
            <button
              type="button"
              data-testid="nav-shop"
              aria-expanded={shopOpen}
              aria-haspopup="true"
              onClick={() => setShopOpen((v) => !v)}
              onFocus={openShop}
              className={`inline-flex items-center gap-1 font-display text-[13px] font-semibold uppercase tracking-[0.14em] transition-colors duration-300 hover:text-regal-amber ${shopOpen || pathname.startsWith("/shop") ? "text-regal-amber" : "text-neutral-300"}`}
            >
              Shop <ChevronDown size={13} className={`transition-transform duration-300 ${shopOpen ? "rotate-180" : ""}`} />
            </button>
            {shopOpen && (
              <div data-testid="shop-mega-menu" className="absolute left-1/2 top-full w-[min(74rem,94vw)] -translate-x-1/2 pt-5" onMouseEnter={openShop} onMouseLeave={closeShop}>
                <div className="border border-line bg-white shadow-[0_32px_64px_rgba(2,6,23,0.28)]">
                  <div className="grid grid-cols-5 divide-x divide-line">
                    {categoryError && <button onClick={retryCategories} data-testid="shop-menu-retry" className="p-7 text-sm">Categories unavailable — retry</button>}
                    {shopMenu.map((col) => (
                      <div key={col.slug} className="p-7" data-testid={`shop-menu-col-${col.slug}`}>
                        <Link to={`/shop/${col.slug}`} onClick={() => setShopOpen(false)} data-testid={`shop-menu-${col.slug}`} className="font-display text-[13px] font-extrabold uppercase tracking-[0.14em] text-ink hover:text-regal-amber transition-colors">
                          {col.name}
                        </Link>
                        <div className="mt-5 flex flex-col items-start gap-3">
                          {col.children.map(({ name: label, href }) => (
                            <Link key={label} to={href} onClick={() => setShopOpen(false)} data-testid={`shop-menu-${col.slug}-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                              className="font-mono text-[11px] uppercase tracking-[0.14em] text-neutral-500 transition-colors hover:text-regal-amber">
                              {label}
                            </Link>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="flex items-center justify-between border-t border-line bg-surface px-7 py-4">
                    <p className="font-mono text-[10px] uppercase tracking-[0.16em] text-neutral-500">Genuine tools, cables & accessories — live stock</p>
                    <Link to="/shop/all" onClick={() => setShopOpen(false)} data-testid="shop-menu-view-all" className="inline-flex items-center gap-2 font-display text-[12px] font-bold uppercase tracking-[0.16em] text-regal-amber hover:text-ink transition-colors">
                      View All Products <ArrowUpRight size={14} />
                    </Link>
                  </div>
                </div>
              </div>
            )}
          </div>
          {LINKS_AFTER_SHOP.map((l) => <NavLink key={l.to} to={l.to} end={l.to === "/"} className={navCls} data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}>{l.label}</NavLink>)}
        </nav>
        <div className="flex items-center gap-2 sm:gap-3">
          <Link
            to="/account"
            data-testid="nav-account-link"
            aria-label="My account"
            className="grid h-11 w-11 place-items-center border border-line text-neutral-300 hover:border-regal-amber hover:text-regal-amber transition-colors duration-300"
          >
            <UserRound size={18} />
          </Link>
          <button
            onClick={() => setOpen(true)}
            data-testid="cart-open-button"
            aria-label="Open shopping cart"
            className="relative grid h-11 w-11 place-items-center border border-line text-neutral-300 hover:border-regal-amber hover:text-regal-amber transition-colors duration-300"
          >
            <ShoppingCart size={18} />
            {count > 0 && (
              <span data-testid="cart-count-badge" className="absolute -right-1.5 -top-1.5 grid h-5 min-w-5 place-items-center bg-regal-amber px-1 font-mono text-[10px] font-bold text-void">{count}</span>
            )}
          </button>
          <a
            href={PORTAL_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nav-file-portal-button"
            className="inline-flex items-center gap-1.5 bg-regal-amber px-3 sm:px-5 h-11 font-display text-[11px] sm:text-[13px] font-bold uppercase tracking-[0.14em] text-white hover:bg-amber-400 transition-colors duration-300"
          >
            <span className="hidden sm:inline">File&nbsp;</span>Portal <ArrowUpRight size={15} />
          </a>
          <button
            className="lg:hidden grid h-11 w-11 place-items-center border border-line text-neutral-200"
            onClick={() => setMobile((v) => !v)}
            aria-label="Toggle menu"
            data-testid="mobile-menu-button"
          >
            {mobile ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>
      {mobile && (
        <div className="lg:hidden glass border-t border-line max-h-[calc(100dvh-4rem)] overflow-y-auto" data-testid="mobile-menu">
          <nav className="container-regal flex flex-col py-4">
            {LINKS_BEFORE_SHOP.map((l) => (
              <NavLink key={l.to} to={l.to} end={l.to === "/"} data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`} className={({ isActive }) => `py-3.5 font-display text-base font-semibold uppercase tracking-[0.14em] border-b border-line/60 ${isActive ? "text-regal-amber" : "text-neutral-200"}`}>
                {l.label}
              </NavLink>
            ))}
            {/* SHOP accordion */}
            <button
              type="button"
              onClick={() => setMobileShop((v) => !v)}
              aria-expanded={mobileShop}
              data-testid="mobile-nav-shop"
              className={`flex items-center justify-between py-3.5 font-display text-base font-semibold uppercase tracking-[0.14em] border-b border-line/60 ${mobileShop || pathname.startsWith("/shop") ? "text-regal-amber" : "text-neutral-200"}`}
            >
              Shop <ChevronDown size={16} className={`transition-transform duration-300 ${mobileShop ? "rotate-180" : ""}`} />
            </button>
            {mobileShop && (
              <div data-testid="mobile-shop-accordion">
                {categoryError && <button onClick={retryCategories} data-testid="mobile-shop-retry" className="py-3 text-sm">Categories unavailable — retry</button>}
                {shopMenu.map((col) => (
                  <div key={col.slug} className="border-b border-line/60">
                    <button
                      type="button"
                      onClick={() => setMobileBrand((b) => (b === col.slug ? null : col.slug))}
                      aria-expanded={mobileBrand === col.slug}
                      data-testid={`mobile-shop-${col.slug}`}
                      className="flex w-full items-center justify-between py-3 pl-4 font-display text-sm font-semibold uppercase tracking-[0.14em] text-neutral-300"
                    >
                      {col.name} <ChevronDown size={14} className={`transition-transform duration-300 ${mobileBrand === col.slug ? "rotate-180 text-regal-amber" : ""}`} />
                    </button>
                    {mobileBrand === col.slug && (
                      <div className="flex flex-col pb-3 pl-8" data-testid={`mobile-shop-${col.slug}-links`}>
                        {col.children.map(({ name: label, href }) => (
                          <Link key={label} to={href} data-testid={`mobile-shop-${col.slug}-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                            className="py-2.5 font-mono text-[12px] uppercase tracking-[0.14em] text-neutral-400 hover:text-regal-amber">
                            {label}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
                <Link to="/shop/all" data-testid="mobile-shop-view-all" className="py-3.5 pl-4 font-display text-sm font-bold uppercase tracking-[0.14em] text-regal-amber border-b border-line/60">
                  View All Products
                </Link>
              </div>
            )}
            {LINKS_AFTER_SHOP.map((l) => (
              <NavLink key={l.to} to={l.to} end={l.to === "/"} data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`} className={({ isActive }) => `py-3.5 font-display text-base font-semibold uppercase tracking-[0.14em] border-b border-line/60 ${isActive ? "text-regal-amber" : "text-neutral-200"}`}>
                {l.label}
              </NavLink>
            ))}
            <NavLink to="/account" data-testid="mobile-nav-account" className={({ isActive }) => `py-3.5 font-display text-base font-semibold uppercase tracking-[0.14em] border-b border-line/60 ${isActive ? "text-regal-amber" : "text-neutral-200"}`}>
              My Account
            </NavLink>
            <a href={PORTAL_URL} target="_blank" rel="noopener noreferrer" data-testid="mobile-file-portal-button" className="mt-4 mb-2 inline-flex items-center justify-center gap-2 bg-regal-amber py-4 font-display text-sm font-bold uppercase tracking-[0.16em] text-void">
              File Portal <ArrowUpRight size={16} />
            </a>
          </nav>
        </div>
      )}
    </header>
  );
}
